import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck, ClipboardList, HeartHandshake } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { business } from "@/data/business";

// Google Forms doesn't allow being iframed if a browser blocks 3rd-party cookies,
// so we surface a fallback link to open the form in a new tab in every case.
const openUrl = business.parentConsentFormUrl.replace("&embedded=true", "").replace("?embedded=true", "?embedded=false");

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Secure & confidential", body: "Submitted directly to School Smiles Australia. Your details are handled in line with our Privacy Policy." },
  { icon: ClipboardList, title: "One form, ready to go", body: "Consent + a short medical/dental history in a single form. Takes most families ~5 minutes." },
  { icon: HeartHandshake, title: "Any questions?", body: "Not sure about a section? Contact us before you submit — we're happy to help." },
];

const ParentConsent = () => {
  if (typeof document !== "undefined") document.title = "Parent Consent Form — School Smiles Australia";

  return (
    <>
      <section className="container-page pt-16 md:pt-24 pb-6" data-testid="consent-hero">
        <div className="max-w-3xl">
          <span className="eyebrow"><ClipboardList size={14} strokeWidth={2.5}/> Parent consent</span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">
            Parent Consent &amp; Medical Information
          </h1>
          <p className="mt-5 text-[#4B6B86] text-lg leading-relaxed">
            Please complete the form below to provide consent for your child's dental care and share the medical and dental history our clinicians need to look after them safely.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="consent-open-new-tab"
            >
              Open form in a new tab
              <ExternalLink size={18} strokeWidth={2.5}/>
            </a>
            <Link to="/for-parents" className="text-sm font-medium text-[#4B6B86] hover:text-[#14324A]">
              Read the parent information first
            </Link>
          </div>
        </div>
      </section>

      <section className="container-page py-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="consent-highlights">
        {HIGHLIGHTS.map((h) => (
          <div key={h.title} className="card-soft p-6 flex gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-[#F4F9FF] text-[#1677C8] inline-flex items-center justify-center">
              <h.icon size={20} strokeWidth={2.5}/>
            </div>
            <div>
              <h3 className="font-display font-semibold text-[#14324A]">{h.title}</h3>
              <p className="mt-1 text-sm text-[#4B6B86] leading-relaxed">{h.body}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="container-page pb-16" data-testid="consent-form-embed">
        <div className="card-soft overflow-hidden">
          <iframe
            title="Parent Consent & Medical Information — School Smiles Australia"
            src={business.parentConsentFormUrl}
            className="w-full block"
            style={{ height: "1600px", border: "0" }}
            loading="lazy"
          >
            Loading…
          </iframe>
        </div>
        <p className="mt-4 text-xs text-[#4B6B86] text-center">
          Having trouble with the embedded form?{" "}
          <a href={openUrl} target="_blank" rel="noopener noreferrer" className="text-[#1677C8] hover:text-[#14324A] font-medium">
            Open it in a new tab
          </a>.
        </p>
      </section>
    </>
  );
};

export default ParentConsent;
